import { useState, useEffect, useCallback } from "react";
import axios from "../axios";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { Flash, Modal, FormField, ModalActions, Pagination } from "../components/AdminUI";
import "../styles/admin.css";

export default function BusManager() {
  const [buses, setBuses] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState({ message: "", type: "success" });

  // フィルター
  const [searchQ, setSearchQ] = useState("");
  const [depotFilter, setDepotFilter] = useState("");
  const [retiredFilter, setRetiredFilter] = useState("active"); // "all" | "active" | "retired"
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 10;

  // 追加・編集モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [editBus, setEditBus] = useState(null);
  const [form, setForm] = useState({ bus_number: "", depot_id: "", retired_at: "" });
  const [saving, setSaving] = useState(false);

  // 削除確認モーダル
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── データ取得 ──
  const loadDepots = useCallback(async () => {
    try {
      const res = await axios.get("/depots");
      setDepots(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDepots([]);
    }
  }, []);

  const loadBuses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/buses");
      setBuses(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      showFlash(e.response?.data?.message ?? "バスの取得に失敗しました", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepots();
    loadBuses();
  }, [loadDepots, loadBuses]);

  function showFlash(message, type = "success") {
    setFlash({ message, type });
  }

  // ── フィルタリング・ページネーション ──
  const isRetired = (b) => b.retired_at && new Date(b.retired_at) <= new Date(new Date().toDateString());

  const filtered = buses.filter((b) => {
    const matchQ = !searchQ || b.bus_number.toLowerCase().includes(searchQ.toLowerCase());
    const matchD = !depotFilter || String(b.depot_id) === String(depotFilter);
    const matchR = retiredFilter === "all"
      || (retiredFilter === "active"  && !isRetired(b))
      || (retiredFilter === "retired" &&  isRetired(b));
    return matchQ && matchD && matchR;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  function handleSearchChange(v) { setSearchQ(v); setCurrentPage(1); }
  function handleDepotChange(v) { setDepotFilter(v); setCurrentPage(1); }

  // ── モーダル操作 ──
  function openAdd() {
    setEditBus(null);
    setForm({ bus_number: "", depot_id: "", retired_at: "" });
    setModalOpen(true);
  }

  function openEdit(bus) {
    setEditBus(bus);
    setForm({
      bus_number: bus.bus_number,
      depot_id:   String(bus.depot_id),
      retired_at: bus.retired_at ? bus.retired_at.slice(0, 10) : "",
    });
    setModalOpen(true);
  }

  function closeModal() { setModalOpen(false); setEditBus(null); }

  async function handleSave() {
    if (!form.bus_number.trim() || !form.depot_id) {
      showFlash("車号と営業所は必須です", "error");
      return;
    }
    setSaving(true);
    const payload = {
      bus_number: form.bus_number.trim(),
      depot_id:   Number(form.depot_id),
      retired_at: form.retired_at || null,
    };
    try {
      if (editBus) {
        await axios.put(`/admin/buses/${editBus.id}`, payload);
        const depot = depots.find((d) => d.id === payload.depot_id);
        setBuses((prev) =>
          prev.map((b) => b.id === editBus.id ? { ...b, ...payload, depot: depot ?? b.depot } : b)
        );
        showFlash("更新しました");
      } else {
        const res = await axios.post("/admin/buses", payload);
        const depot = depots.find((d) => d.id === payload.depot_id);
        setBuses((prev) => [{ ...res.data, depot: depot ?? null }, ...prev]);
        showFlash("追加しました");
      }
      closeModal();
    } catch (e) {
      showFlash(e.response?.data?.message ?? "保存に失敗しました", "error");
    } finally {
      setSaving(false);
    }
  }

  // ── 削除 ──
  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`/admin/buses/${deleteTarget.id}`);
      setBuses((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      showFlash("削除しました");
      setDeleteTarget(null);
    } catch (e) {
      showFlash(e.response?.data?.message ?? "削除に失敗しました", "error");
    } finally {
      setDeleting(false);
    }
  }

  // ── Render ──
  return (
    <div className="adm-page">
      <AdminBreadcrumb items={[{ label: "車両管理" }]} />

      <Flash message={flash.message} type={flash.type} onClose={() => setFlash({ ...flash, message: "" })} />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">車両管理</h2>
          <span className="adm-badge adm-badge--amber">admin</span>
        </div>
        <button className="adm-btn adm-btn--primary" onClick={openAdd}>
          ＋ 追加
        </button>
      </div>

      {/* フィルター */}
      <div className="adm-filter-box">
        <div className="adm-filter-row">
          <div className="adm-filter-field">
            <span className="adm-filter-label">車号検索</span>
            <input
              type="text"
              className="adm-input"
              placeholder="例: 1234"
              value={searchQ}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ minWidth: 140 }}
            />
          </div>
          <div className="adm-filter-field">
            <span className="adm-filter-label">営業所</span>
            <select
              className="adm-select"
              value={depotFilter}
              onChange={(e) => handleDepotChange(e.target.value)}
              style={{ minWidth: 130 }}
            >
              <option value="">すべて</option>
              {depots.map((d) => (
                <option key={d.id} value={d.id}>{d.short_name}</option>
              ))}
            </select>
          </div>
          <div className="adm-filter-field">
            <span className="adm-filter-label">ステータス</span>
            <select
              className="adm-select"
              value={retiredFilter}
              onChange={(e) => { setRetiredFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="active">稼働中のみ</option>
              <option value="retired">廃車のみ</option>
              <option value="all">すべて</option>
            </select>
          </div>
        </div>
      </div>

      {/* テーブル */}
      <div className="adm-card">
        <div className="adm-card__header">
          車両一覧
          <span className="adm-card__header-sub">{filtered.length} 件</span>
        </div>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                {["ID", "車号", "営業所", "ステータス", "廃車日", "操作"].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="adm-empty">読み込み中…</td></tr>
              ) : pageItems.length === 0 ? (
                <tr><td colSpan={6} className="adm-empty">該当するバスがありません</td></tr>
              ) : (
                pageItems.map((bus) => (
                  <tr key={bus.id} style={isRetired(bus) ? { opacity: 0.55 } : {}}>
                    <td className="adm-table__id">{bus.id}</td>
                    <td className="adm-table__bold">{bus.bus_number}</td>
                    <td>
                      <span className="adm-badge adm-badge--blue">
                        {bus.depot?.short_name ?? ""}
                      </span>
                    </td>
                    <td>
                      {isRetired(bus)
                        ? <span className="adm-badge adm-badge--red">廃車</span>
                        : <span className="adm-badge adm-badge--green">稼働中</span>
                      }
                    </td>
                    <td className="adm-table__mute adm-nowrap" style={{ fontSize: ".75rem" }}>
                      {bus.retired_at ? String(bus.retired_at).slice(0, 10) : "—"}
                    </td>
                    <td>
                      <div className="adm-gap-sm">
                        <button
                          className="adm-btn adm-btn--default adm-btn--sm"
                          onClick={() => openEdit(bus)}
                        >
                          編集
                        </button>
                        <button
                          className="adm-btn adm-btn--danger adm-btn--sm"
                          onClick={() => setDeleteTarget(bus)}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          current={safePage}
          total={totalPages}
          onChange={setCurrentPage}
          count={filtered.length}
        />
      </div>

      {/* 追加・編集モーダル */}
      <Modal show={modalOpen} title={editBus ? "バスを編集" : "バスを追加"} onClose={closeModal}>
        <FormField label="車号" required>
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.bus_number}
            onChange={(e) => setForm((f) => ({ ...f, bus_number: e.target.value }))}
            placeholder="例: 1234"
            maxLength={20}
          />
        </FormField>
        <FormField label="営業所" required>
          <select
            className="adm-select adm-select--full"
            value={form.depot_id}
            onChange={(e) => setForm((f) => ({ ...f, depot_id: e.target.value }))}
          >
            <option value="">選択してください</option>
            {depots.map((d) => (
              <option key={d.id} value={d.id}>{d.short_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="廃車日">
          <input
            type="date"
            className="adm-input adm-input--full"
            value={form.retired_at}
            onChange={(e) => setForm((f) => ({ ...f, retired_at: e.target.value }))}
          />
          <span className="adm-field-hint">入力すると該当日以降の目撃投稿が不可になります。空白で稼働中扱い。</span>
        </FormField>
        <ModalActions
          onCancel={closeModal}
          onConfirm={handleSave}
          confirmLabel={saving ? "保存中…" : "保存"}
          disabled={saving}
        />
      </Modal>

      {/* 削除確認モーダル */}
      <Modal show={!!deleteTarget} title="削除の確認" size="sm" onClose={() => setDeleteTarget(null)}>
        <p className="adm-modal__body">以下のバスを削除しますか？この操作は取り消せません。</p>
        {deleteTarget && (
          <p className="adm-modal__confirm-target">
            車号: {deleteTarget.bus_number}（{deleteTarget.depot?.short_name ?? ""}）
          </p>
        )}
        <ModalActions
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          confirmLabel={deleting ? "削除中…" : "削除する"}
          confirmClass="adm-btn--danger"
          disabled={deleting}
        />
      </Modal>
    </div>
  );
}
